import { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Download, ShoppingCart, Users, Truck, Boxes,
  CreditCard, RotateCcw, Tag, BarChart3, FileText, Plug, Bell, Settings,
  LogOut, Ship, Store, MessageSquare,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  ["/admin", "Dashboard", LayoutDashboard],
  ["/admin/products", "Products", Package],
  ["/admin/import", "Import Products", Download],
  ["/admin/orders", "Orders", ShoppingCart],
  ["/admin/customers", "Customers", Users],
  ["/admin/messages", "Messages", MessageSquare],
  ["/admin/suppliers", "Suppliers", Truck],
  ["/admin/inventory", "Inventory", Boxes],
  ["/admin/shipping", "Shipping", Ship],
  ["/admin/payments", "Payments", CreditCard],
  ["/admin/returns", "Returns", RotateCcw],
  ["/admin/discounts", "Discounts", Tag],
  ["/admin/analytics", "Analytics", BarChart3],
  ["/admin/reports", "Reports", FileText],
  ["/admin/integrations", "API Integrations", Plug],
  ["/admin/notifications", "Notifications", Bell],
  ["/admin/settings", "Settings", Settings],
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user === false) nav("/admin/login");
    else if (user && user.kind !== "admin") nav("/admin/login");
  }, [user, nav]);

  if (!user || user.kind !== "admin") return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading…</div>;

  return (
    <div className="min-h-screen flex bg-zinc-50">
      <aside className="w-60 bg-zinc-950 text-zinc-300 flex flex-col fixed h-screen">
        <div className="px-5 py-5 border-b border-zinc-800">
          <div className="font-display text-xl font-extrabold text-white tracking-tight">Qava<span className="text-[#FF6A1A]">.</span> <span className="text-[10px] font-normal text-zinc-500 align-top">ADMIN</span></div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
          {NAV.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/admin"}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-white text-zinc-900 font-medium" : "hover:bg-zinc-800/70 text-zinc-400"}`}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon size={17} />{label}
            </NavLink>
          ))}
        </nav>
        <div className="px-2.5 py-3 border-t border-zinc-800 space-y-0.5">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:bg-zinc-800/70"><Store size={17} />View Store</a>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:bg-zinc-800/70 w-full" data-testid="admin-logout"><LogOut size={17} />Sign out</button>
        </div>
      </aside>

      <div className="flex-1 ml-60">
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-end px-6 sticky top-0 z-30">
          <div className="text-right">
            <p className="text-sm font-medium leading-none">{user.name}</p>
            <p className="text-[11px] text-zinc-500 mt-0.5">{user.role?.replace(/_/g, " ")}</p>
          </div>
          <div className="w-8 h-8 bg-zinc-900 text-white rounded-full flex items-center justify-center text-xs font-semibold ml-3">{user.name?.[0]}</div>
        </header>
        <div className="p-6"><Outlet /></div>
      </div>
    </div>
  );
}

export function money(usd) {
  return `$${(usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function StatusBadge({ status }) {
  const map = {
    "Delivered": "bg-green-100 text-green-700", "Shipped": "bg-blue-100 text-blue-700",
    "In Transit": "bg-indigo-100 text-indigo-700", "Processing": "bg-indigo-100 text-indigo-700",
    "Supplier Order Created": "bg-indigo-100 text-indigo-700",
    "Awaiting Fulfillment": "bg-amber-100 text-amber-700", "Payment Pending": "bg-zinc-100 text-zinc-600",
    "Paid": "bg-green-100 text-green-700", "Cancelled": "bg-red-100 text-red-700",
    "Refunded": "bg-zinc-200 text-zinc-700", "Requires Review": "bg-red-100 text-red-700",
    "active": "bg-green-100 text-green-700", "out_of_stock": "bg-red-100 text-red-700",
  };
  return <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${map[status] || "bg-zinc-100 text-zinc-600"}`}>{String(status).replace(/_/g, " ")}</span>;
}
